import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Pencil, Trash2, UserCog } from 'lucide-react';

export default function VaultEntityCard({
  isLight,
  title,
  description,
  tag,
  icon: Icon,
  iconClass,
  inactive,
  onOpen,
  onEdit,
  onDelete,
  onPermissions,
  deleteDisabled,
  onTagClick,
  tagTitle,
}: {
  isLight: boolean;
  title: string;
  description: string;
  tag: string;
  icon: LucideIcon;
  iconClass: string;
  inactive?: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onPermissions?: () => void;
  deleteDisabled?: boolean;
  onTagClick?: () => void;
  tagTitle?: string;
}) {
  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      className={`group relative flex flex-col rounded-2xl p-5 text-left cursor-pointer transition-all ${
        isLight
          ? 'bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ring-1 ring-black/[0.04] hover:shadow-[0_8px_24px_rgba(16,24,40,0.08)] hover:-translate-y-0.5'
          : 'bg-neutral-900 ring-1 ring-white/10 hover:ring-white/20'
      } ${inactive ? 'opacity-70' : ''}`}
    >
      <div className="flex items-center gap-3 min-w-0 pr-16">
        <span className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${iconClass}`}>
          <Icon size={18} />
        </span>
        <h3 className={`text-[15px] font-semibold tracking-tight truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>
          {title}
        </h3>
      </div>

      <p className={`mt-3 text-sm leading-relaxed line-clamp-2 min-h-[2.5rem] ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
        {description}
      </p>

      <div className={`mt-4 pt-4 border-t ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
        {onTagClick ? (
          <button
            type="button"
            title={tagTitle}
            onClick={(event) => {
              event.stopPropagation();
              onTagClick();
            }}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${
              isLight ? 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200' : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
            }`}
          >
            {tag}
          </button>
        ) : (
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${
              isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-800 text-neutral-300'
            }`}
          >
            {tag}
          </span>
        )}
      </div>

      <div
        className={`absolute top-3.5 right-3.5 flex items-center gap-0.5 rounded-xl p-0.5 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto transition-opacity ${
          isLight ? 'bg-white/90 shadow-sm ring-1 ring-black/5' : 'bg-neutral-800 ring-1 ring-white/10'
        }`}
      >
        <button
          type="button"
          className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-500 hover:text-indigo-600 hover:bg-indigo-50' : 'text-neutral-400 hover:text-white hover:bg-neutral-700'}`}
          aria-label="Edit"
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
        >
          <Pencil size={14} />
        </button>
        {onPermissions ? (
          <button
            type="button"
            className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-500 hover:text-indigo-600 hover:bg-indigo-50' : 'text-neutral-400 hover:text-white hover:bg-neutral-700'}`}
            aria-label="Permissions"
            onClick={(event) => {
              event.stopPropagation();
              onPermissions();
            }}
          >
            <UserCog size={14} />
          </button>
        ) : null}
        <button
          type="button"
          className={`p-1.5 rounded-lg ${isLight ? 'text-neutral-500 hover:text-red-600 hover:bg-red-50' : 'text-neutral-400 hover:text-red-400 hover:bg-neutral-700'}`}
          aria-label="Remove"
          disabled={deleteDisabled}
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </article>
  );
}
